import { FingerConfig, FingerType } from '@/types';
import {
  CourseTrainingConfiguration,
  ComplexityMode,
  HandType
} from '@/types/wordTraining';
import { getCourseTrainingConfiguration } from '@/services/wordTrainingService';
import { getCourseRound } from '@/data/courseStructure';
import { getKeysForFingers, getAllActiveFingers } from '@/data/fingerKeyMapping';

/**
 * Course Training Integration Service
 * 
 * Handles the integration between saved course configurations and the actual training system.
 * This service acts as the bridge between admin configurations and game engine execution.
 */

/**
 * Gets the effective training configuration for a specific day/round
 * Checks for saved admin configuration first, falls back to default course structure
 */
export const getEffectiveTrainingConfiguration = async (
  levelNumber: number,
  dayNumber: number,
  roundNumber: number
): Promise<{
  fingerConfig: FingerConfig;
  trainingMode?: { complexity: ComplexityMode; hands: 'left' | 'right' | 'both' };
  isCustom: boolean;
  customConfig?: CourseTrainingConfiguration;
}> => {
  console.log(`🔍 Looking for custom configuration: Level ${levelNumber}, Day ${dayNumber}, Round ${roundNumber}`);

  try {
    // Check for saved admin configuration first
    const customConfig = await getCourseTrainingConfiguration(levelNumber, dayNumber, roundNumber);

    if (customConfig) {
      console.log(`✅ Found custom configuration for Day ${dayNumber}, Round ${roundNumber}`);
      console.log('Custom config:', {
        fingers: customConfig.fingerCombination,
        hand: customConfig.hand,
        complexity: customConfig.complexity,
        selectionMode: customConfig.trainingSelection.mode
      });

      // Convert saved configuration to FingerConfig
      const fingerConfig = convertCustomConfigToFingerConfig(customConfig);

      // Create training mode for game engine
      const trainingMode: { complexity: ComplexityMode; hands: 'left' | 'right' | 'both' } = {
        complexity: customConfig.complexity,
        hands: (customConfig.hand === 'left' || customConfig.hand === 'right') ? customConfig.hand : 'both'
      };

      return {
        fingerConfig,
        trainingMode,
        isCustom: true,
        customConfig
      };
    }
  } catch (error) {
    console.warn(`⚠️ Error loading custom configuration for Day ${dayNumber}, Round ${roundNumber}:`, error);
  }

  // Fall back to default course structure
  console.log(`📚 Using default course structure for Level ${levelNumber}, Day ${dayNumber}, Round ${roundNumber}`);
  const defaultRound = getCourseRound(levelNumber, dayNumber, roundNumber);

  if (!defaultRound) {
    throw new Error(`No course configuration found for Day ${dayNumber}, Round ${roundNumber}`);
  }

  return {
    fingerConfig: defaultRound.fingerConfig,
    isCustom: false
  };
};

/**
 * Converts a saved custom configuration to a FingerConfig for the game engine
 */
export const convertCustomConfigToFingerConfig = (
  config: CourseTrainingConfiguration
): FingerConfig => {
  console.log('🔄 Converting custom configuration to FingerConfig:', config);

  // Convert finger abbreviations back to FingerType arrays
  const fingerTypes = convertFingerAbbreviationsToTypes(
    config.fingerCombination,
    config.hand
  );

  // Separate by hand
  const activeFingersLeft = fingerTypes.filter(f => f.startsWith('left')) as FingerType[];
  const activeFingersRight = fingerTypes.filter(f => f.startsWith('right')) as FingerType[];

  // Convert complexity to letter complexity range
  const letterComplexity = convertComplexityToRange(config.complexity);

  const fingerConfig: FingerConfig = {
    activeFingersLeft,
    activeFingersRight,
    letterComplexity
  };

  console.log('✅ Converted FingerConfig:', {
    leftFingers: activeFingersLeft,
    rightFingers: activeFingersRight,
    complexity: letterComplexity
  });

  return fingerConfig;
};

/**
 * Converts finger abbreviations (T, I, M, R, P) back to FingerType arrays
 */
export const convertFingerAbbreviationsToTypes = (
  abbreviations: string[],
  hand: HandType
): FingerType[] => {
  const fingerMap: Record<string, { left: FingerType; right: FingerType }> = {
    T: { left: 'leftThumb', right: 'rightThumb' },
    I: { left: 'leftPointing', right: 'rightPointing' },
    M: { left: 'leftMiddle', right: 'rightMiddle' },
    R: { left: 'leftRing', right: 'rightRing' },
    P: { left: 'leftPinky', right: 'rightPinky' }
  };

  const fingers: FingerType[] = [];

  abbreviations.forEach(abbrev => {
    const fingerPair = fingerMap[abbrev];
    if (fingerPair) {
      if (hand === 'left') {
        fingers.push(fingerPair.left);
      } else if (hand === 'right') {
        fingers.push(fingerPair.right);
      } else {
        // Should not happen with new unified system, but handle gracefully
        fingers.push(fingerPair.left, fingerPair.right);
      }
    }
  });

  return fingers;
};

/**
 * Converts ComplexityMode to letter complexity range
 */
export const convertComplexityToRange = (
  complexity: ComplexityMode
): { min: number; max: number } => {
  if (complexity === 'words') {
    // For word mode, use a higher complexity for the underlying letter system
    // This allows the word generation to work with longer sequences
    // The actual word generation will be handled by the training mode
    return { min: 1, max: 5 }; // CHANGED: Allow up to 5 letter complexity for word chunks
  }

  // For letter modes, use the complexity as max
  return { min: 1, max: complexity as number };
};

/**
 * Validates that a finger configuration is usable for training
 */
export const validateTrainingConfiguration = (
  fingerConfig: FingerConfig
): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  // Check if any fingers are active
  const allActiveFingers = getAllActiveFingers(fingerConfig);
  if (allActiveFingers.length === 0) {
    errors.push('No active fingers configured');
  }

  // Check if there are available keys
  try {
    const availableKeys = getKeysForFingers(allActiveFingers);
    if (availableKeys.length === 0) {
      errors.push('No available keys for selected fingers');
    }
  } catch (error) {
    errors.push('Error determining available keys for fingers');
  }

  // Check complexity range
  if (fingerConfig.letterComplexity.min > fingerConfig.letterComplexity.max) {
    errors.push('Invalid complexity range: min greater than max');
  }

  if (fingerConfig.letterComplexity.min < 1) {
    errors.push('Complexity minimum must be at least 1');
  }

  if (fingerConfig.letterComplexity.max > 5) {
    errors.push('Complexity maximum cannot exceed 5');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Gets a summary of what fingers and keys will be used in training
 */
export const getTrainingConfigurationSummary = (
  fingerConfig: FingerConfig,
  trainingMode?: { complexity: ComplexityMode; hands: 'left' | 'right' | 'both' }
) => {
  const allActiveFingers = getAllActiveFingers(fingerConfig);
  const availableKeys = getKeysForFingers(allActiveFingers);

  return {
    activeFingers: allActiveFingers,
    availableKeys: availableKeys.map(key => key === 'SPACE' ? '_' : key),
    leftFingers: fingerConfig.activeFingersLeft,
    rightFingers: fingerConfig.activeFingersRight,
    letterComplexity: fingerConfig.letterComplexity,
    trainingMode: trainingMode?.complexity || 'letters',
    handMode: trainingMode?.hands || 'both'
  };
};

/**
 * Checks if a configuration has been customized for a specific day/round
 */
export const hasCustomConfiguration = async (
  levelNumber: number,
  dayNumber: number,
  roundNumber: number
): Promise<boolean> => {
  try {
    const config = await getCourseTrainingConfiguration(levelNumber, dayNumber, roundNumber);
    return config !== null;
  } catch (error) {
    console.warn(`Error checking for custom configuration Day ${dayNumber}, Round ${roundNumber}:`, error);
    return false;
  }
};

/**
 * Gets all customized days and rounds
 */
export const getCustomizedDaysAndRounds = async (): Promise<Array<{ level: number; day: number; round: number }>> => {
  try {
    const { getAllCourseTrainingConfigurations } = await import('@/services/wordTrainingService');
    const configs = await getAllCourseTrainingConfigurations();

    return configs.map((config: any) => ({
      level: config.levelNumber || 1,
      day: config.dayNumber,
      round: config.roundNumber
    }));
  } catch (error) {
    console.error('Error getting customized days and rounds:', error);
    return [];
  }
};

/**
 * Debug helper to log the effective configuration
 */
export const logTrainingConfiguration = (
  dayNumber: number,
  roundNumber: number,
  config: {
    fingerConfig: FingerConfig;
    trainingMode?: { complexity: ComplexityMode; hands: 'left' | 'right' | 'both' };
    isCustom: boolean;
  }
) => {
  console.log(`🎯 Training Configuration for Day ${dayNumber}, Round ${roundNumber}:`);
  console.log(`   Custom: ${config.isCustom ? 'YES' : 'NO'}`);
  console.log(`   Fingers: ${getAllActiveFingers(config.fingerConfig).join(', ')}`);
  console.log(`   Complexity: ${config.trainingMode?.complexity || 'default'}`);
  console.log(`   Hand Mode: ${config.trainingMode?.hands || 'both'}`);
  console.log(`   Letter Range: ${config.fingerConfig.letterComplexity.min}-${config.fingerConfig.letterComplexity.max}`);

  const summary = getTrainingConfigurationSummary(config.fingerConfig, config.trainingMode);
  console.log(`   Available Keys: ${summary.availableKeys.join(', ')}`);
};
